import { Checkbox, Button } from "@mui/material";
import { useEffect, useState } from "react";
import { PublishProfileExportConfigToLensFamilyDocument } from "generated/graphql";
import { runGqlMutation } from "gqlClient";
import { PublishInfo, PublishModal } from "routes/lens-editor/PublishModal";

export interface Props {
  exportConfigName: string;
  configId: number;
  lensId: number;
  buttonDisabled?: boolean;
  coversheetFilename?: string;
}
export const PublishExportConfigModalWithButton = ({
  exportConfigName,
  configId,
  lensId,
  buttonDisabled,
  coversheetFilename,
}: Props) => {
  const [lensInfo, setLensInfo] = useState<PublishInfo[] | undefined>();
  const [error, setError] = useState(false);
  const [overwriteCoversheetTemplate, setOverwriteCoversheetTemplate] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [dryRun, setDryRun] = useState<boolean>(true);
  const [wasPublished, setWasPublished] = useState<boolean>(false);

  const subtitle = `Publish ${exportConfigName} to the following lenses?`;

  const resetState = () => {
    setLensInfo(undefined);
    setOverwriteCoversheetTemplate(false);
    setDryRun(true);
    setWasPublished(false);
    setError(false);
  };

  const publishExportConfig = (dryRun: boolean, targetLensIds: number[]) => {
    setDryRun(dryRun);
    runGqlMutation(PublishProfileExportConfigToLensFamilyDocument, {
      configId,
      lensId,
      targetLensIds,
      dryRun,
      overwriteCoversheetTemplate: overwriteCoversheetTemplate,
    })
      .then((data) => {
        if (data.lens.publishProfileExportConfig.updateInfo) {
          setLensInfo(
            data.lens.publishProfileExportConfig.updateInfo.map((info) => ({
              clientId: info.clientId,
              clientName: info.clientName,
              lensId: info.lensId,
              lensName: info.lensName,
              errors: info.errors,
              noIssueText: info.configAlreadyExists ? (
                <p>Override settings for {exportConfigName}</p>
              ) : (
                <p>Create new Config {exportConfigName}</p>
              ),
            }))
          );
        }
      })
      .catch(() => setError(true));
  };

  const handleClose = () => {
    setOpen(false);
    if (wasPublished) {
      resetState();
      publishExportConfig(true, []);
    }
  };

  const handlePublish = async (lensIds: number[]) => {
    publishExportConfig(false, lensIds);
    setWasPublished(true);
  };

  useEffect(() => {
    publishExportConfig(true, []);
  }, []);

  const options = (
    <div className="px-6 pt-4">
      <p className="text-base font-bold mb-2">Optional Config</p>
      <div className="flex items-center">
        <div className="flex items-center min-w-[300px]">
          <Checkbox
            size="small"
            checked={overwriteCoversheetTemplate}
            onChange={(e) => setOverwriteCoversheetTemplate(e.target.checked)}
          />
          <p onClick={() => setOverwriteCoversheetTemplate((prev) => !prev)} className="ml-2 text-sm select-none">
            Overwrite Custom Coversheet Template
          </p>
        </div>
        <p className="ml-4 text-sm text-gray-500 font-mono">{coversheetFilename}</p>
      </div>
    </div>
  );

  if (error) return null;

  return (
    lensInfo && (
      <>
        <Button
          disabled={buttonDisabled ?? true}
          className="mr-2 ml-2"
          color="primary"
          variant="outlined"
          onClick={() => setOpen(true)}>
          Publish Export Config
        </Button>
        <PublishModal
          options={options}
          open={open}
          error=""
          dryrun={dryRun}
          isDisabled={false}
          updateInfo={lensInfo}
          close={handleClose}
          publish={handlePublish}
          header="Publish Export Config"
          text={subtitle}
        />
      </>
    )
  );
};
export default PublishExportConfigModalWithButton;
